import type {
    CompensationComputedView,
    CompensationCycle,
    CompensationCycleBudgetRequest,
    CompensationCycleOverview,
    CompensationDraftRequest,
    CompensationEnvelope,
    CompensationEnvelopeCreateRequest,
    CompensationSalaryParseResult,
    CompensationSaveRequest,
    CompensationVersion,
} from '@/types/compensation';

import { api } from '@/api/api';

export default {
    async getCycles(): Promise<CompensationCycle[]> {
        const response = await api.get('/CompensationEnvelope/Cycles');
        return response.data;
    },

    async getCycleOverview(cycleEvaluationId: string): Promise<CompensationCycleOverview> {
        const response = await api.get('/CompensationEnvelope', { params: { cycleEvaluationId } });
        return response.data;
    },

    async setCycleBudget(cycleId: string, request: CompensationCycleBudgetRequest): Promise<void> {
        await api.put(`/CompensationEnvelope/Cycles/${cycleId}/Budget`, request);
    },

    async createEnvelope(
        request: CompensationEnvelopeCreateRequest,
    ): Promise<CompensationEnvelope> {
        const response = await api.post('/CompensationEnvelope', request);
        return response.data;
    },

    async getEnvelope(id: string): Promise<CompensationEnvelope> {
        const response = await api.get(`/CompensationEnvelope/${id}`);
        return response.data;
    },

    async previewEnvelope(
        id: string,
        request: CompensationDraftRequest,
    ): Promise<CompensationComputedView> {
        const response = await api.post(`/CompensationEnvelope/${id}/Preview`, request);
        return response.data;
    },

    async saveEnvelope(
        id: string,
        request: CompensationSaveRequest,
    ): Promise<CompensationEnvelope> {
        const response = await api.put(`/CompensationEnvelope/${id}`, request);
        return response.data;
    },

    async deleteEnvelope(id: string): Promise<void> {
        await api.delete(`/CompensationEnvelope/${id}`);
    },

    async closeEnvelope(id: string): Promise<CompensationEnvelope> {
        const response = await api.post(`/CompensationEnvelope/${id}/Close`);
        return response.data;
    },

    async reopenEnvelope(id: string): Promise<CompensationEnvelope> {
        const response = await api.post(`/CompensationEnvelope/${id}/Reopen`);
        return response.data;
    },

    async getVersions(id: string): Promise<CompensationVersion[]> {
        const response = await api.get(`/CompensationEnvelope/${id}/Versions`);
        return response.data;
    },

    async restoreVersion(id: string, versionId: string): Promise<CompensationEnvelope> {
        const response = await api.post(
            `/CompensationEnvelope/${id}/Versions/${versionId}/Restore`,
        );
        return response.data;
    },

    async exportEnvelope(id: string): Promise<Blob> {
        const response = await api.get<Blob>(`/CompensationEnvelope/${id}/Export`, {
            responseType: 'blob',
        });
        return response.data;
    },

    async parseSalaries(id: string, content: string): Promise<CompensationSalaryParseResult> {
        const response = await api.post(`/CompensationEnvelope/${id}/Salaries/Parse`, { content });
        return response.data;
    },
};
